import { useRef, useState } from "react";
import { X } from "lucide-react";
import { hhmm, minutesOf } from "@/lib/time";

export const H_START = 60;
export const H_END = 24 * 60;
export const H_PX = 40;
const SNAP = 15;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

function normalize(list) {
  const sorted = [...list].filter((x) => x.b - x.a >= SNAP).sort((x, y) => x.a - y.a);
  const out = [];
  sorted.forEach((x) => {
    const last = out[out.length - 1];
    if (last && x.a <= last.b) last.b = Math.max(last.b, x.b);
    else out.push({ ...x });
  });
  return out.map((x) => ({ start: hhmm(x.a), end: hhmm(x.b) }));
}

function compute(d, m) {
  const list = d.base.map((x) => ({ ...x }));
  if (d.mode === "new") {
    const a = Math.min(d.origin, m);
    list.push({ a, b: Math.max(Math.max(d.origin, m), a + SNAP) });
  } else if (d.mode === "move") {
    const len = list[d.idx].b - list[d.idx].a;
    const a = clamp(list[d.idx].a + (m - d.origin), H_START, H_END - len);
    list[d.idx] = { a, b: a + len };
  } else if (d.mode === "bottom") {
    list[d.idx].b = Math.max(list[d.idx].a + SNAP, m);
  } else {
    list[d.idx].a = Math.min(list[d.idx].b - SNAP, m);
  }
  return list;
}

export default function DayEditor({ dayKey, blocks, onChange }) {
  const ref = useRef(null);
  const drag = useRef(null);
  const [preview, setPreview] = useState(null);
  const base = blocks.map((b) => ({ a: minutesOf(b.start), b: minutesOf(b.end) }));
  const shown = preview || base;

  const toMin = (y) => {
    const r = ref.current.getBoundingClientRect();
    return clamp(Math.round((H_START + ((y - r.top) / H_PX) * 60) / SNAP) * SNAP, H_START, H_END);
  };
  const begin = (e, mode, idx) => {
    e.stopPropagation();
    e.preventDefault();
    drag.current = { mode, idx, origin: toMin(e.clientY), base };
    ref.current.setPointerCapture(e.pointerId);
    setPreview(compute(drag.current, drag.current.origin));
  };
  const move = (e) => {
    if (drag.current) setPreview(compute(drag.current, toMin(e.clientY)));
  };
  const end = (e) => {
    if (!drag.current) return;
    const list = compute(drag.current, toMin(e.clientY));
    drag.current = null;
    setPreview(null);
    onChange(normalize(list));
  };
  const remove = (i) => onChange(normalize(base.filter((_, j) => j !== i)));

  return (
    <div ref={ref} onPointerDown={(e) => begin(e, "new")} onPointerMove={move} onPointerUp={end}
      className="relative border-l border-[#103080]/10 bg-[#103080]/[0.03] cursor-crosshair select-none touch-none"
      style={{ height: ((H_END - H_START) / 60) * H_PX }} data-testid={`hours-day-${dayKey}`}>
      {shown.map((x, i) => (
        <div key={i} onPointerDown={(e) => begin(e, "move", i)} data-testid={`hours-block-${dayKey}-${i}`}
          className="absolute left-1 right-1 rounded-lg bg-[#0090D0]/15 border border-[#0090D0]/50 text-[#103080] cursor-grab active:cursor-grabbing"
          style={{ top: ((x.a - H_START) / 60) * H_PX, height: ((x.b - x.a) / 60) * H_PX }}>
          <div onPointerDown={(e) => begin(e, "top", i)} className="absolute inset-x-0 top-0 h-2 cursor-ns-resize" />
          <p className="text-[11px] font-semibold px-2 pt-1.5 leading-tight">{hhmm(x.a)}–{hhmm(x.b)}</p>
          {!preview && (
            <button onPointerDown={(e) => e.stopPropagation()} onClick={() => remove(i)} aria-label="Remove block"
              data-testid={`hours-block-remove-${dayKey}-${i}`}
              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white/80 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors duration-200">
              <X size={11} />
            </button>
          )}
          <div onPointerDown={(e) => begin(e, "bottom", i)} className="absolute inset-x-0 bottom-0 h-2 cursor-ns-resize" />
        </div>
      ))}
    </div>
  );
}
